import { SearchUserEnriched } from 'lib-search-types';
import { useMemo } from 'react';

import { useAppSelector } from 'utils/redux/useAppSelector';

const selectPresenceOverlay = (state: {
  presenceOverlay: { members: Record<string, Partial<SearchUserEnriched>> };
}) => state.presenceOverlay.members;

/**
 * Merges presence overlay data with search results.
 *
 * This hook reads from the Redux presenceOverlay slice and merges any overlay
 * updates with the provided members array. This allows real-time presence
 * updates to be displayed without mutating RTK Query cache.
 *
 * @param members - Array of SearchUserEnriched from RTK Query (can be readonly)
 * @returns Array of SearchUserEnriched with presence overlay data merged in
 */
export const useMembersWithPresence = <T extends SearchUserEnriched>(
  members: readonly T[],
): T[] => {
  const presenceOverlay = useAppSelector(selectPresenceOverlay);

  return useMemo(() => {
    return members.map((member) => {
      const overlay = presenceOverlay[member.id];
      if (!overlay) return member;
      return { ...member, ...overlay };
    });
  }, [members, presenceOverlay]);
};
